/**
 * Script Generation readiness — mirrors skill
 * ``script_generation/v1/input.schema.json`` required fields as the SoT:
 *   required: creative_context{title, premise}, script_count
 *
 * ``script_count`` is NOT a user field — request builders / backend inject
 * GENERATE=1 / DIVERGE=3. Never surface ``creative_context`` / ``script_count``
 * internal names to tenants.
 */

import type { WorkspaceProviderOption } from '../../../api'
import {
  findProviderOptionById,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from './capabilityProviderSelection'
import {
  buildGenerationReadiness,
  type GenerationReadiness,
  type GenerationBlockingReason,
} from './generationReadiness'
import {
  normalizeScriptGenerationFieldDefaults,
  SCRIPT_GENERATION_DEFAULT_CONTENT_TYPE,
  SCRIPT_GENERATION_DEFAULT_TARGET_DURATION_SEC,
} from './scriptGenerationContract'
import type { ScriptCreativeContextField } from './scriptGenerationCanvas'
import { isScriptSourceInputReady, resolveScriptSourceContext } from './scriptSourceContext'

/** Matches backend ``GENERATE_SCRIPT_COUNT`` / skill schema minimum. */
export const SCRIPT_GENERATION_NORMAL_SCRIPT_COUNT = 1
/** Matches backend ``DIVERGE_SCRIPT_COUNT`` / Canonical Generation Card triplet. */
export const SCRIPT_GENERATION_DIVERGE_SCRIPT_COUNT = 3

export const SCRIPT_GENERATION_SKILL_KEY = 'script_generation'

export {
  SCRIPT_GENERATION_DEFAULT_CONTENT_TYPE,
  SCRIPT_GENERATION_DEFAULT_TARGET_DURATION_SEC,
  normalizeScriptGenerationFieldDefaults,
}

/** Skill-schema required: script source title + premise/text (minLength 1). */
export function isScriptCreativeContextInputReady(
  creativeContext: ScriptCreativeContextField | null | undefined,
): boolean {
  if (!creativeContext) return false
  const title = String(creativeContext.title ?? '').trim()
  const premise = String(creativeContext.premise ?? '').trim()
  return title.length > 0 && premise.length > 0
}

export function isScriptSourceContextInputReady(fields: Record<string, unknown>): boolean {
  const ctx = resolveScriptSourceContext(fields)
  return isScriptSourceInputReady(ctx)
}

export function evaluateScriptGenerationInputReadiness(
  fields: Record<string, unknown>,
): GenerationReadiness {
  const scriptSource = resolveScriptSourceContext(fields)
  const blockingReasons: GenerationBlockingReason[] = []
  if (!isScriptSourceInputReady(scriptSource)) {
    blockingReasons.push({
      code: 'CREATIVE_CONTEXT_REQUIRED',
      field: 'script_source_context',
      messageKey: scriptSource
        ? 'scriptGeneration.creativeContextIncomplete'
        : 'scriptGeneration.connectCreativeFirst',
      dimension: 'INPUT_READY',
    })
  }
  // contentType / targetDuration are USER_REQUIRED with system defaults —
  // after normalization they are always present; only block if caller
  // explicitly cleared duration to null/0 without a creatable default path.
  const normalized = normalizeScriptGenerationFieldDefaults(fields)
  if (!normalized.contentType.trim()) {
    blockingReasons.push({
      code: 'CONTENT_TYPE_REQUIRED',
      field: 'content_type',
      messageKey: 'scriptGeneration.contentTypeRequired',
      dimension: 'INPUT_READY',
    })
  }
  if (!(normalized.targetDurationSeconds > 0)) {
    blockingReasons.push({
      code: 'TARGET_DURATION_REQUIRED',
      field: 'target_duration_seconds',
      messageKey: 'scriptGeneration.targetDurationRequired',
      dimension: 'INPUT_READY',
    })
  }
  return buildGenerationReadiness({ blockingReasons })
}

export function evaluateScriptGenerationProviderReadiness(args: {
  fields: Record<string, unknown>
  providerOptions: WorkspaceProviderOption[]
  loading: boolean
  error: boolean
}): GenerationReadiness {
  const blockingReasons: GenerationBlockingReason[] = []
  if (args.loading) {
    return buildGenerationReadiness({ blockingReasons })
  }
  if (args.error || args.providerOptions.length === 0) {
    blockingReasons.push({
      code: 'NO_AVAILABLE_PROVIDER',
      messageKey: 'scriptGeneration.noAvailableProvider',
      dimension: 'PROVIDER_READY',
    })
    return buildGenerationReadiness({ blockingReasons })
  }
  const anyAvailable = args.providerOptions.some(isProviderOptionSelectable)
  if (!anyAvailable) {
    blockingReasons.push({
      code: 'NO_AVAILABLE_PROVIDER',
      messageKey: 'scriptGeneration.noAvailableProvider',
      dimension: 'PROVIDER_READY',
    })
    return buildGenerationReadiness({ blockingReasons })
  }
  const selectionMode = normalizeProviderSelectionMode(args.fields.providerSelectionMode)
  const resolvedId = resolveCapabilityProviderOptionId({
    selectionMode,
    storedProviderOptionId: (args.fields.providerOptionId as string | null) ?? null,
    options: args.providerOptions,
  })
  const resolved = findProviderOptionById(args.providerOptions, resolvedId)
  if (!resolved || !isProviderOptionSelectable(resolved)) {
    blockingReasons.push({
      code: 'NO_AVAILABLE_PROVIDER',
      messageKey: 'scriptGeneration.noAvailableProvider',
      dimension: 'PROVIDER_READY',
    })
  }
  return buildGenerationReadiness({ blockingReasons })
}

export function evaluateScriptGenerationSkillReadiness(fields: Record<string, unknown>): GenerationReadiness {
  const blockingReasons: GenerationBlockingReason[] = []
  const source = String(fields.skillSelectionSource || 'AUTO').toUpperCase()
  if (source === 'EXPLICIT') {
    const key = String(fields.skillKey || '').trim()
    if (key && key !== SCRIPT_GENERATION_SKILL_KEY) {
      blockingReasons.push({
        code: 'SKILL_UNAVAILABLE',
        field: 'skill_key',
        messageKey: 'scriptGeneration.skillUnavailable',
        dimension: 'SKILL_READY',
      })
    }
  }
  return buildGenerationReadiness({ blockingReasons })
}

export function evaluateScriptGenerationRuntimeReadiness(isBusy: boolean): GenerationReadiness {
  if (!isBusy) return buildGenerationReadiness({})
  return buildGenerationReadiness({
    blockingReasons: [
      {
        code: 'RUNTIME_BUSY',
        messageKey: 'scriptGeneration.runtimeBusy',
        dimension: 'RUNTIME_READY',
      },
    ],
  })
}

/** Full pre-submit gate for Script Generate / Diverge / Quick Action. */
export function evaluateScriptGenerationReadiness(args: {
  fields: Record<string, unknown>
  providerOptions: WorkspaceProviderOption[]
  providerLoading: boolean
  providerError: boolean
  isBusy: boolean
}): GenerationReadiness {
  const input = evaluateScriptGenerationInputReadiness(args.fields)
  const provider = evaluateScriptGenerationProviderReadiness({
    fields: args.fields,
    providerOptions: args.providerOptions,
    loading: args.providerLoading,
    error: args.providerError,
  })
  const skill = evaluateScriptGenerationSkillReadiness(args.fields)
  const runtime = evaluateScriptGenerationRuntimeReadiness(args.isBusy)
  const blockingReasons = [
    ...input.blockingReasons,
    ...provider.blockingReasons,
    ...skill.blockingReasons,
    ...runtime.blockingReasons,
  ]
  return buildGenerationReadiness({
    blockingReasons,
    warnings: [...input.warnings, ...provider.warnings, ...skill.warnings, ...runtime.warnings],
  })
}

/** True when Quick Action may create+link a Script node (same script-source SoT). */
export function isCreativePayloadReadyForScriptLink(payload: {
  title?: string | null
  premise?: string | null
} | null): boolean {
  if (!payload) return false
  return String(payload.title ?? '').trim().length > 0 && String(payload.premise ?? '').trim().length > 0
}

/** Text Asset Quick Action readiness — non-empty persisted text. */
export function isTextAssetPayloadReadyForScriptLink(text: string | null | undefined): boolean {
  return String(text ?? '').trim().length > 0
}
